import React, { useState } from "react";
import { AppHeader } from "./CommunitySelection";
import { FINAL_VISION, VISION_IMG } from "../lib/constants";
import { createLesson } from "../lib/api";
import { useLang } from "../lib/i18n";
import { MapPin, RotateCcw, Check, Lightbulb, ArrowUpRight } from "lucide-react";

export const CommunityReveal = ({ summary, sessionId, onContinue }) => {
  const { t } = useLang();
  const [name, setName] = useState("");
  const [lesson, setLesson] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const shareLesson = async () => {
    if (!lesson.trim() || sent || busy) return;
    setBusy(true);
    try {
      await createLesson({ name: name.trim(), text: lesson.trim(), community_name: summary.community_name });
      setSent(true);
    } catch (e) {
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen">
      <AppHeader subtitle={summary.community_name} />
      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-10 sm:py-12">
        <div className="animate-rise">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-vin/70 mb-3">
            {t("The community revealed")}
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-ink leading-tight">
            {t("You were experiencing the data")}
          </h2>
          <p className="mt-4 text-base text-ink-soft leading-relaxed">
            {t("Everything you just experienced was shaped by realistic conditions inspired by publicly available data about")} {summary.community_name}. {t("The statistics were intentionally hidden. Instead of reading the numbers, you felt what those conditions can mean for a family.")}
          </p>
        </div>

        <div className="mt-8 bg-white rounded-lg border border-[var(--line)] p-6 sm:p-8">
          <div className="flex items-center gap-2 text-vin mb-5">
            <MapPin className="w-4 h-4" strokeWidth={1.75} />
            <span className="text-[11px] uppercase tracking-[0.14em] font-semibold">
              {summary.community_name} · {t("Community snapshot")}
            </span>
          </div>
          <ul className="space-y-3">
            {summary.snapshot.map((s, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-ink leading-relaxed">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-vin shrink-0" />
                {s}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-6 bg-vin/5 border border-vin/15 rounded-lg p-6 sm:p-7">
          <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-vin-dark/70 mb-4">
            {t("Conditions that shaped this journey")}
          </p>
          <ul className="space-y-3">
            {summary.community_conditions.map((c, i) => (
              <li key={i} className="text-sm text-vin-dark leading-relaxed flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-vin shrink-0" />
                {c}
              </li>
            ))}
          </ul>
        </div>

        {/* Final vision */}
        <div className="mt-8 relative overflow-hidden rounded-lg bg-vin-dark">
          <img
            src={VISION_IMG}
            alt="Volunteers working together"
            className="absolute inset-0 w-full h-full object-cover opacity-25"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-vin-dark/70" aria-hidden="true" />
          <div className="relative z-10 p-7 sm:p-9">
            <p className="text-lg text-white leading-relaxed font-body">{t(FINAL_VISION)}</p>
          </div>
        </div>

        {/* Lessons Learned */}
        <div className="mt-8 bg-white rounded-lg border border-vin/20 p-6 sm:p-7">
          <div className="flex items-center gap-2 text-vin mb-3">
            <Lightbulb className="w-4 h-4" strokeWidth={1.75} />
            <span className="text-[11px] uppercase tracking-[0.14em] font-semibold">{t("Lessons Learned")}</span>
          </div>
          <label className="text-[15px] font-semibold text-ink block" htmlFor="lesson-text">
            {t("What did walking this year help you understand?")}
          </label>
          <p className="text-sm text-ink-soft mt-1">
            {t("Your reflection joins a shared wall where participants read and respond to one another.")}
          </p>
          {sent ? (
            <div
              data-testid="lesson-thanks"
              className="mt-4 flex items-start gap-2 text-sm text-[#365C4C] bg-[#365C4C]/8 border border-[#365C4C]/20 rounded-lg p-4"
            >
              <Check className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2} />
              <span>
                {t("Thank you for sharing. Your lesson will appear on the wall once reviewed.")}{" "}
                <a href="/lessons" target="_blank" rel="noopener noreferrer" className="font-semibold underline underline-offset-2">
                  {t("Visit the Lessons Learned wall")}
                </a>.
              </span>
            </div>
          ) : (
            <div className="mt-4">
              <input
                data-testid="lesson-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("First name or initials (optional)")}
                maxLength={40}
                className="w-full rounded-lg border border-[var(--line)] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-vin/40 focus:border-vin"
              />
              <textarea
                id="lesson-text"
                data-testid="lesson-input"
                value={lesson}
                onChange={(e) => setLesson(e.target.value)}
                rows={3}
                placeholder={t("Share what you learned…")}
                className="mt-3 w-full rounded-lg border border-[var(--line)] p-4 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-vin/40 focus:border-vin resize-none"
              />
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <button
                  data-testid="share-lesson-button"
                  onClick={shareLesson}
                  disabled={!lesson.trim() || busy}
                  className="inline-flex items-center gap-2 bg-vin text-white font-semibold px-6 py-2.5 rounded-full hover:bg-vin-hover transition-colors duration-200 disabled:opacity-40"
                >
                  {busy ? t("Sharing…") : t("Share Lessons Learned")}
                </button>
                <a
                  href="/lessons"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="reveal-lessons-link"
                  className="inline-flex items-center gap-1 text-vin text-sm font-semibold hover:underline underline-offset-4"
                >
                  {t("See the wall")} <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                </a>
              </div>
            </div>
          )}
        </div>

        <div className="mt-10 pt-8 border-t border-[var(--line)] text-center">
          <button
            data-testid="reveal-continue-button"
            onClick={onContinue}
            className="inline-flex items-center gap-2 bg-vin text-white font-semibold px-7 py-3.5 rounded-full hover:bg-vin-hover transition-colors duration-200"
          >
            {t("Begin the conversation")}
            <RotateCcw className="w-4 h-4 hidden" strokeWidth={1.75} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommunityReveal;
